import {
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  Heading,
  HStack,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { LayoutTemplateIcon, TrashIcon, XIcon } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { type FlowTemplate, type FlowTemplateSummary, flowTemplatesApi } from '@/lib/api'
import { useColorModeValue } from '~/color-mode'
import { toaster } from '~/toaster'

interface TemplateGalleryProps {
  onClose: () => void
  onSelectTemplate: (template: FlowTemplate) => void
}

function TemplateGallery({ onClose, onSelectTemplate }: TemplateGalleryProps) {
  const { hasMinRole } = useAuth()
  const queryClient = useQueryClient()
  const isAdmin = hasMinRole('admin')
  const emptyStateIconColor = useColorModeValue('#777672', '#BBB9B5')

  // Fetch available templates
  const { data: templates, isLoading } = useQuery({
    queryKey: ['flow-templates'],
    queryFn: () => flowTemplatesApi.list(),
  })

  const loadMutation = useMutation({
    mutationFn: (slug: string) => flowTemplatesApi.getBySlug(slug),
    onSuccess: (template) => {
      onSelectTemplate(template)
      onClose()
    },
    onError: (error: Error) => {
      toaster.create({
        title: 'Failed to load template',
        description: error.message,
        type: 'error',
      })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => flowTemplatesApi.delete(id),
    onSuccess: () => {
      toaster.create({
        title: 'Template deleted',
        type: 'success',
      })
      queryClient.invalidateQueries({ queryKey: ['flow-templates'] })
    },
    onError: (error: Error) => {
      toaster.create({
        title: 'Failed to delete template',
        description: error.message,
        type: 'error',
      })
    },
  })

  const handleSelectTemplate = (template: FlowTemplateSummary) => {
    loadMutation.mutate(template.slug)
  }

  const handleDeleteTemplate = (e: React.MouseEvent, template: FlowTemplateSummary) => {
    e.stopPropagation()
    if (confirm(`Delete template "${template.name}"? This action cannot be undone.`)) {
      deleteMutation.mutate(template.id)
    }
  }

  // Industry color mapping
  const getIndustryColor = (industry?: string) => {
    switch (industry?.toLowerCase()) {
      case 'automotive':
        return 'blue'
      case 'energy':
        return 'green'
      case 'manufacturing':
        return 'orange'
      case 'logistics':
        return 'purple'
      case 'oil & gas':
        return 'red'
      case 'pharmaceutical':
        return 'cyan'
      case 'smart building':
        return 'teal'
      default:
        return 'gray'
    }
  }

  return (
    <Box
      position="fixed"
      inset={0}
      bg="blackAlpha.600"
      zIndex={2000}
      onClick={onClose}
      overflow="auto"
    >
      <Box
        position="relative"
        mx="auto"
        my={8}
        bg="bg.panel"
        borderRadius="lg"
        w="90vw"
        maxW="900px"
        maxH="80vh"
        overflow="hidden"
        display="flex"
        flexDirection="column"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <Flex justify="space-between" align="center" p={4} borderBottomWidth="1px">
          <Heading size="md">Choose a Template</Heading>
          <Button size="sm" variant="ghost" onClick={onClose}>
            <XIcon size={16} />
          </Button>
        </Flex>

        {/* Content */}
        <Box flex={1} overflow="auto" p={4}>
          {isLoading ? (
            <Flex justify="center" py={8}>
              <Spinner />
            </Flex>
          ) : !templates || templates.length === 0 ? (
            <VStack py={8} gap={2}>
              <LayoutTemplateIcon size={48} color={emptyStateIconColor} />
              <Text color="content.secondary" textAlign="center">
                No templates available
              </Text>
            </VStack>
          ) : (
            <Grid
              templateColumns={{
                base: '1fr',
                md: 'repeat(2, 1fr)',
                lg: 'repeat(3, 1fr)',
              }}
              gap={4}
            >
              {templates.map((template) => (
                <Box
                  key={template.id}
                  p={4}
                  borderWidth="1px"
                  borderRadius="lg"
                  cursor="pointer"
                  transition="all 0.2s"
                  _hover={{
                    bg: 'gray.50',
                    borderColor: 'blue.400',
                    shadow: 'md',
                    _dark: { bg: 'gray.700' },
                  }}
                  onClick={() => handleSelectTemplate(template)}
                  opacity={loadMutation.isPending ? 0.5 : 1}
                >
                  {/* Thumbnail placeholder */}
                  <Box
                    bg="bg.subtle"
                    borderRadius="md"
                    h="100px"
                    mb={3}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    overflow="hidden"
                  >
                    {template.thumbnailUrl ? (
                      <img
                        src={template.thumbnailUrl}
                        alt={template.name}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                    ) : (
                      <LayoutTemplateIcon size={32} color={emptyStateIconColor} />
                    )}
                  </Box>

                  {/* Template info */}
                  <VStack align="start" gap={1}>
                    <HStack justify="space-between" w="full">
                      <Text fontWeight="semibold" lineClamp={1} flex={1}>
                        {template.name}
                      </Text>
                      {isAdmin && (
                        <Button
                          size="xs"
                          variant="ghost"
                          colorPalette="red"
                          onClick={(e) => handleDeleteTemplate(e, template)}
                          disabled={deleteMutation.isPending}
                          p={1}
                          minW="auto"
                        >
                          <TrashIcon size={14} />
                        </Button>
                      )}
                    </HStack>
                    {template.industry && (
                      <Badge size="sm" colorPalette={getIndustryColor(template.industry)}>
                        {template.industry}
                      </Badge>
                    )}
                    {template.description && (
                      <Text fontSize="sm" color="content.secondary" lineClamp={2}>
                        {template.description}
                      </Text>
                    )}
                  </VStack>
                </Box>
              ))}
            </Grid>
          )}
        </Box>

        {/* Footer */}
        <Flex justify="flex-end" p={4} borderTopWidth="1px">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </Flex>
      </Box>
    </Box>
  )
}

export default TemplateGallery
